import { useUpdateProp } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import ShadowStyle from 'client/components/core/Sidebar/components/Settings/styles/ShadowStyle'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { headerColorsKeys } from 'src/editor/layout/sidebar/website-styles-settings/consts'
import { useCurrentTheme } from 'src/editor/layout/sidebar/website-styles-settings/hooks/useCurrentTheme'
import { ImageControl } from 'src/editor/shared/settings/controls/image-control'
import { PaddingControl } from 'src/editor/shared/settings/controls/padding-control'
import { ColorPickerGroup } from 'src/editor/shared/settings/groups/color-picker'
import { WebsiteHeaderInterface } from 'src/shared/types/website-entities'
import { SizeAndPositionGroup } from '../../settings/groups/size-and-position'
import { EntityBorderGroup } from '../../settings/wrappers/border-groups/entity-border-group'

interface WebsiteHeaderDesignProps {
  entity: WebsiteHeaderInterface
}

export const WebsiteHeaderDesign = ({ entity }: WebsiteHeaderDesignProps) => {
  const { t } = useTranslation()
  const updateProp = useUpdateProp(entity)
  const { currentTheme, onUpdate } = useCurrentTheme()

  return (
    <div className="pb-8">
      <GroupTitle className="mb-6" withoutHorizontalLine>
        {t('website_styles_settings.colors.label')}
      </GroupTitle>
      <ColorPickerGroup
        colorKeys={headerColorsKeys}
        elementKey="headerAndFooter"
        labelClassName="basis-auto"
        singleDeviceMode
      />
      <ImageControl
        label="website_theme_settings.group.header.background_picture"
        labelClassName="basis-auto"
        fileId={currentTheme.headerAndFooter.headerBackgroundPicture.file?.id}
        updateBackgroundFile={file =>
          onUpdate({
            headerAndFooter: {
              ...currentTheme?.headerAndFooter,
              headerBackgroundPicture: {
                ...currentTheme.headerAndFooter.headerBackgroundPicture,
                fileId: file?.id,
                file,
              },
            },
          })
        }
        update={background => {
          onUpdate({
            headerAndFooter: {
              ...currentTheme?.headerAndFooter,
              headerBackgroundPicture: {
                ...currentTheme.headerAndFooter.headerBackgroundPicture,
                background,
              },
            },
          })
        }}
        backgroundPosition={
          currentTheme.headerAndFooter.headerBackgroundPicture.background
        }
      />
      <EntityBorderGroup
        entity={entity}
        propertyKey="border"
        mobilePropertyKey="mobileBorder"
      />

      <SizeAndPositionGroup
        contentWidth={entity.contentWidth}
        updateContentWidth={updateProp('contentWidth')}
        sticky={entity?.sticky}
        htmlAttrId={entity.htmlAttrId}
        updateSticky={updateProp('sticky')}
        stickyOptions={{
          title: '',
          label: t('website_settings.sidebar.website_header.sticky'),
          tooltipText: t('settings_options.sticky.help_message'),
          isShowSelect: false,
          singleDeviceMode: true,
        }}
      />

      <GroupTitle className="mb-6" withoutHorizontalLine>
        {t('settings_styles.padding.label')}
      </GroupTitle>
      <PaddingControl
        padding={entity?.padding}
        mobilePadding={entity?.mobilePadding}
        update={updateProp('padding')}
        mobileUpdate={updateProp('mobilePadding')}
        label="settings_styles.padding.top"
        propertyKey="paddingTop"
        max={100}
      />
      <PaddingControl
        padding={entity?.padding}
        mobilePadding={entity?.mobilePadding}
        update={updateProp('padding')}
        mobileUpdate={updateProp('mobilePadding')}
        label="settings_styles.padding.bottom"
        propertyKey="paddingBottom"
        max={100}
      />
      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('mobileBoxShadow')}
        withoutHorizontalLine
      />
    </div>
  )
}
